import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Avatar, Box, Button, Card, CardContent, Chip, IconButton, InputAdornment,
  Snackbar, Alert, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SendIcon from "@mui/icons-material/Send";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { packagesApi, occupantsApi } from "../api/resources";
import DeliverDialog from "../components/DeliverDialog";
import ConversationDialog from "../components/packages/ConversationDialog";
import { useLanguage } from "../i18n/LanguageContext";
import { describeNotifyResult } from "../utils/notify";
import { COLORS } from "../theme";

const courierLabel = (p) => (p.courier === "Otro" && p.courierNote ? `Otro (${p.courierNote})` : p.courier);

export default function PanelPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState([]);
  const [deliveredToday, setDeliveredToday] = useState(0);
  const [totalDelivered, setTotalDelivered] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState("");
  const [deliverPkg, setDeliverPkg] = useState(null);
  const [unitOccupants, setUnitOccupants] = useState([]);
  const [conversationPkg, setConversationPkg] = useState(null);
  const [toast, setToast] = useState(null);

  const fmtDateTime = (iso) =>
    new Date(iso).toLocaleString(lang === "en" ? "en-US" : "es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  const elapsed = (iso) => {
    const ms = Date.now() - new Date(iso).getTime();
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h < 1) return `${m} ${t("panel.minAgo")}`;
    if (h < 24) return `${h} ${t("panel.hAgo")}`;
    return `${Math.floor(h / 24)} ${t("panel.dAgo")}`;
  };

  const openDeliver = async (pkg) => {
    setDeliverPkg(pkg);
    const list = await occupantsApi.list(pkg.unitId);
    setUnitOccupants(list.filter((o) => o.unit === pkg.unitId));
  };

  const load = () => {
    packagesApi.list({ status: "pendiente" }).then(setPending);
    packagesApi.list({ status: "entregado" }).then((all) => {
      setTotalDelivered(all.length);
      const today = new Date().toDateString();
      setDeliveredToday(all.filter((p) => new Date(p.deliveredAt).toDateString() === today).length);
    });
  };

  useEffect(() => {
    load();
    if (location.state?.toast) {
      setToast({ severity: "success", message: location.state.toast });
      window.history.replaceState({}, document.title);
    }
  }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q) return pending;
    const term = q.toLowerCase();
    return pending.filter((p) => `${p.unitId} ${p.occupantId?.name || ""}`.toLowerCase().includes(term));
  }, [pending, q]);

  const handleDeliver = async (id, payload) => {
    const result = await packagesApi.deliver(id, payload);
    setDeliverPkg(null);
    load();
    setToast({
      severity: result.notify?.sent ? "success" : "warning",
      message: `${t("panel.deliveredNoNotify")} ${describeNotifyResult(result.notify, t)}`,
    });
  };

  const handleCopyLink = async (pkg) => {
    const url = `${window.location.origin}/rastreo/${pkg.trackingToken}`;
    await navigator.clipboard.writeText(url);
    setToast({ severity: "success", message: t("panel.linkCopied") });
  };

  const handleResend = async (pkg) => {
    const result = await packagesApi.resend(pkg._id);
    setToast({
      severity: result.sent ? "success" : "warning",
      message: result.sent ? `${t("panel.resent")} ${describeNotifyResult(result, t)}` : (describeNotifyResult(result, t) || t("panel.resendFailed")),
    });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">{t("panel.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("panel.subtitle")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={() => setShowSearch((s) => !s)} sx={{ border: `1px solid ${COLORS.line}` }}>
            <SearchIcon fontSize="small" />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/registrar")}>
            {t("panel.registerPackage")}
          </Button>
        </Stack>
      </Stack>

      {showSearch && (
        <TextField
          size="small"
          autoFocus
          placeholder={t("panel.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ mb: 2, maxWidth: 320 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
        {[
          { label: t("panel.pending"), value: pending.length },
          { label: t("panel.deliveredToday"), value: deliveredToday },
          { label: t("panel.totalHistoric"), value: totalDelivered },
        ].map((s) => (
          <Card key={s.label} sx={{ minWidth: 130 }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="h5">{s.value}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                {s.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#F4F5EF" }}>
              <TableCell>{t("panel.colDept")}</TableCell>
              <TableCell>{t("panel.colPackage")}</TableCell>
              <TableCell>{t("panel.colRecipient")}</TableCell>
              <TableCell>{t("panel.colWait")}</TableCell>
              <TableCell align="right">{t("panel.colAction")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  {pending.length === 0 ? t("panel.emptyNoPending") : t("panel.emptyNoResults")}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((p) => {
              const thumb = p.photos?.[0];
              return (
                <TableRow key={p._id} hover>
                  <TableCell>
                    <Chip label={p.unitId} size="small" sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {thumb && <Avatar variant="rounded" src={thumb} sx={{ width: 40, height: 40 }} />}
                      <Box>
                        <Typography variant="body2">
                          {courierLabel(p)} — {p.desc || t("common.photo")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("panel.receivedBy", fmtDateTime(p.receivedAt), p.receivedBy)}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{p.occupantId?.name || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                      label={elapsed(p.receivedAt)}
                      size="small"
                      sx={{ bgcolor: COLORS.brassSoft, color: "#8A5B18" }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" startIcon={<EditNoteIcon />} onClick={() => openDeliver(p)}>
                        {t("panel.deliverBtn")}
                      </Button>
                      <IconButton size="small" title={t("panel.resendTitle")} onClick={() => handleResend(p)}>
                        <SendIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title={t("panel.conversationTitle")} onClick={() => setConversationPkg(p)}>
                        <ChatBubbleOutlineIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title={t("panel.copyLinkTitle")} onClick={() => handleCopyLink(p)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <DeliverDialog
        open={!!deliverPkg}
        pkg={deliverPkg}
        sameUnitOccupants={unitOccupants.length ? unitOccupants : (deliverPkg?.occupantId ? [deliverPkg.occupantId] : [])}
        onClose={() => setDeliverPkg(null)}
        onConfirm={handleDeliver}
      />

      <ConversationDialog
        pkg={conversationPkg}
        onClose={() => setConversationPkg(null)}
        onSent={() => setToast({ severity: "success", message: t("panel.messageSent") })}
      />

      <Snackbar open={!!toast} autoHideDuration={5500} onClose={() => setToast(null)}>
        {toast && <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert>}
      </Snackbar>
    </Box>
  );
}
